import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { PlainButton } from 'components/v3';
import Glyph from '../Glyph';
import { ORANGE, sx } from '../sx';

// The entry card's Download menu (Implementation List §2): PDF or Excel for
// just this entry. A custom menu, not a native control. The card owns
// `open` so Esc can close the menu before it closes the card.
// `extraItems` ([{ key, icon, label, sub, run }], default none) adds rows
// after Excel, e.g. the W-9's "IRS Form W-9 (PDF)" (w9-plan §6.3 item 9).

const pop = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
`;

const Wrap = styled.div`
  position: relative;
`;

export const BarButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 10px 0 12px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$on ? ORANGE : sx(p.theme).line2)};
  color: ${(p) => (p.$on ? sx(p.theme).text : sx(p.theme).text2)};
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
  transition: color 120ms ease, border-color 120ms ease;

  &:hover {
    color: ${(p) => sx(p.theme).text};
    border-color: ${ORANGE};
  }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  width: 280px;
  padding: 6px;
  box-sizing: border-box;
  background: ${(p) => sx(p.theme).card};
  border: 1px solid ${(p) => sx(p.theme).line2};
  border-radius: 12px;
  box-shadow: ${(p) => sx(p.theme).pop};
  animation: ${pop} 140ms ease;
`;

const MenuHead = styled.div`
  padding: 6px 8px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${(p) => sx(p.theme).label};
`;

const Item = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  padding: 9px 8px;
  border-radius: 8px;
  line-height: 1.35;
  text-align: left;

  &:hover,
  &:focus-visible {
    background: ${(p) => sx(p.theme).tint};
    box-shadow: none;
  }
`;

const ItemIcon = styled.span`
  flex: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => sx(p.theme).fill};
  color: ${(p) => sx(p.theme).text2};

  ${Item}:hover & {
    background: rgba(250, 70, 22, 0.18);
    color: ${ORANGE};
  }
`;

const ItemText = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-size: 13.5px;
    font-weight: 700;
    color: ${(p) => sx(p.theme).text};
  }

  span {
    font-size: 12px;
    color: ${(p) => sx(p.theme).label};
  }
`;

const DownloadMenu = ({
  reference,
  open,
  onOpenChange,
  onPdf,
  onExcel,
  compact,
  extraItems,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOpenChange(false);
    };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [open, onOpenChange]);

  const item = (icon, label, sub, run, key) => (
    <Item
      key={key}
      role="menuitem"
      onClick={() => {
        onOpenChange(false);
        run();
      }}
    >
      <ItemIcon>
        <Glyph name={icon} size={16} />
      </ItemIcon>
      <ItemText>
        <strong>{label}</strong>
        <span>{sub}</span>
      </ItemText>
    </Item>
  );

  return (
    <Wrap ref={ref}>
      <BarButton
        $on={open}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Download this entry"
        data-tour="entry-download"
        onClick={() => onOpenChange(!open)}
      >
        <Glyph name="download" size={15} />
        {compact ? null : <span>Download</span>}
        <Glyph name="chevron-down" size={13} />
      </BarButton>
      {open ? (
        <Menu role="menu" aria-label={`Download ${reference}`}>
          <MenuHead aria-hidden="true">{`Download ${reference}`}</MenuHead>
          {item('printer', 'PDF', 'Formatted to print or save', onPdf)}
          {item('sheet', 'Excel', '.xlsx with one row per field', onExcel)}
          {(extraItems || []).map((extra) =>
            item(
              extra.icon || 'download',
              extra.label,
              extra.sub,
              extra.run,
              extra.key || extra.label
            )
          )}
        </Menu>
      ) : null}
    </Wrap>
  );
};

DownloadMenu.propTypes = {
  reference: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onPdf: PropTypes.func.isRequired,
  onExcel: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  extraItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      icon: PropTypes.string,
      label: PropTypes.string.isRequired,
      sub: PropTypes.string,
      run: PropTypes.func.isRequired,
    })
  ),
};

DownloadMenu.defaultProps = { compact: false, extraItems: undefined };

export default DownloadMenu;
